import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMemoryServiceGetQuerySuggestionsQuery } from '../api/generated-api';
import type { SidekickChipConfig } from '../components/SidekickSuggestionsCard';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../translations';
import {
  buildSuggestionTextById,
  mapSuggestionsToChips,
} from '../utils/suggestionChips';
import {
  DOCK_FALLBACK_CHIPS,
  DOCK_SUGGESTIONS_LIMIT,
} from './dockSuggestions.constants';

/** Optional parameters for {@link useDockSuggestions}. */
export type UseDockSuggestionsParams = {
  /**
   * When `true`, the query is parked on `skipToken` and the fallback
   * chips are returned. Hosts pass `true` while the dock is not
   * mounted — flags off, or an account without a telematics plan —
   * so the request never fires.
   */
  skip?: boolean;
};

export type UseDockSuggestionsResult = {
  /**
   * Chips for the expanded dock. Service-driven when the request
   * returns usable suggestions, otherwise the package fallback set.
   * Every chip carries a resolved `label`, so the dock needs no
   * consumer namespace to render them.
   */
  chips: ReadonlyArray<SidekickChipConfig>;
  /** `true` during the initial fetch. Skip paths report `false`. */
  isLoading: boolean;
  /** `true` when the suggestions query has errored. */
  isError: boolean;
  /** Lookup of `chipId → text` for dispatching the clicked question. */
  suggestionTextById: Record<string, string>;
};

/**
 * Supplies the docked Sidekick's suggestion chips from the memory
 * service, falling back to a package-owned set.
 *
 * The fallback covers both an errored request and a fulfilled one
 * with no usable suggestions (a new account has no query history),
 * so the dock never renders an empty chip column.
 *
 * Per repo rules the hook stays presentation-free: it surfaces
 * `isError` rather than toasting, leaving that call to the UI.
 *
 * @param params - Optional skip gate.
 * @returns Chips, request status, and a `chipId → text` lookup.
 */
export const useDockSuggestions = ({
  skip = false,
}: UseDockSuggestionsParams = {}): UseDockSuggestionsResult => {
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { data, isLoading, isError } = useMemoryServiceGetQuerySuggestionsQuery(
    skip ? skipToken : { limit: DOCK_SUGGESTIONS_LIMIT }
  );

  const fallbackChips = useMemo<ReadonlyArray<SidekickChipConfig>>(
    () =>
      DOCK_FALLBACK_CHIPS.map((chip) => ({
        id: chip.id,
        label: String(t(chip.translationKey ?? '')),
      })),
    [t]
  );

  const chips = useMemo<ReadonlyArray<SidekickChipConfig>>(() => {
    const apiChips = mapSuggestionsToChips(data?.suggestions);
    return apiChips.length > 0 ? apiChips : fallbackChips;
  }, [data, fallbackChips]);

  const suggestionTextById = useMemo(
    () => buildSuggestionTextById(chips),
    [chips]
  );

  return { chips, isLoading, isError, suggestionTextById };
};

export default useDockSuggestions;
